<script setup lang="ts">
import { dashboardApiClient } from '@api-client/dashboard';
import type { ResType } from '@api-client/typeUtils';
import { onMounted, ref } from 'vue';
import { Button } from '@/components/ui/button';
import { Plus, Check, X } from 'lucide-vue-next';
import { toast } from '@/ui-plus/sonner';
import type { ServiceFee } from '@lib/zod/serviceFee';
import ServiceFeeDialog from '../ServiceFeeDialog.vue';
import { Badge } from '@/components/ui/badge';
import { zmgPublicCacheClient } from '@api-client/zmg_cache';
import { zmgAdminOnlyCacheUpdateClient } from '@api-client/zmg_cache';


type StoresResponse = ResType<
    typeof dashboardApiClient.website.manage_stores.list[':websiteId']['$get']
>["data"];

const props = defineProps<{
    currentWebsiteId: string;
    store: StoresResponse['stores'][number];
    isListingAllStores: boolean;
    isServiceFeeEditable: boolean;
}>();

const moreStoreDetails = ref<{
    overrideMarketplaceFee?: ServiceFee;
    isListed?: boolean;
    isRestricted?: boolean;
}>({
    overrideMarketplaceFee: undefined,
    isListed: false,
    isRestricted: false,
});



const loadStoreDetails = async () => {
    const { data: blocklistData, error: blocklistError } = await (await zmgPublicCacheClient.website.store_blocklist[':id'][":cId"]["data.json"].$get({
        param: { id: props.currentWebsiteId, cId: props.store.id },
    })).json();
    if (blocklistError) {
        console.error(blocklistError);
        toast.error('Failed to load store blocklist');
    }
    if (blocklistData) {
        moreStoreDetails.value.isRestricted = true;
    }
    const { data, error } = await (await zmgPublicCacheClient.website.store_allowlist[':id'][":cId"]["data.json"].$get({
        param: { id: props.currentWebsiteId, cId: props.store.id },
    })).json();
    if (error) {
        console.error(error);
        toast.error('Failed to load store allowlist');
    }
    if (data) {
        console.log('data', data);
        moreStoreDetails.value.overrideMarketplaceFee = data.overrideMarketplaceFee ?? undefined
        moreStoreDetails.value.isListed = true;
    }
}

onMounted(loadStoreDetails);

const upsertStore = async (storeId: string, overrideMarketplaceFee?: ServiceFee) => {
    const { error } = await (await zmgAdminOnlyCacheUpdateClient.website.store_allowlist[':id'][":cId"].$patch({
        param: { id: props.currentWebsiteId, cId: storeId },
        json: { overrideMarketplaceFee: overrideMarketplaceFee ?? null }
    })).json();

    if (error) {
        console.error(error);
        toast.error('Failed to save store');
    } else {
        toast.success('saved');
        // update listed field in stores array
        moreStoreDetails.value.isListed = true;
    }
};

const removeStore = async (storeId: string) => {
    const { error } = await (await zmgAdminOnlyCacheUpdateClient.website.store_allowlist[':id'][":cId"].$delete({
        param: { id: props.currentWebsiteId, cId: storeId },
    })).json();

    if (error) {
        console.error(error);
        toast.error('Failed to remove store');
    } else {
        toast.success('Store removed from website');
        // update listed field in stores array
        moreStoreDetails.value.isListed = false;
    }
};

const blockStore = async (storeId: string) => {
    const { error } = await (await zmgAdminOnlyCacheUpdateClient.website.store_blocklist[':id'][":cId"].$patch({
        param: { id: props.currentWebsiteId, cId: storeId },
        json: {}
    })).json();

    if (error) {
        console.error(error);
        toast.error('Failed to restrict store');
    } else {
        toast.success('Store restricted on website');
        // update listed field in stores array
        moreStoreDetails.value.isRestricted = true;
    }
};

const unblockStore = async (storeId: string) => {
    const { error } = await (await zmgAdminOnlyCacheUpdateClient.website.store_blocklist[':id'][":cId"].$delete({
        param: { id: props.currentWebsiteId, cId: storeId },
    })).json();

    if (error) {
        console.error(error);
        toast.error('Failed to allow store');
    } else {
        toast.success('Store allowed on website');
        // update listed field in stores array
        moreStoreDetails.value.isRestricted = false;
    }
};
</script>

<template>
    <div class="flex flex-col">
        <span class="font-medium">{{ store.name }}</span>
        <span v-if="store.address1"
              class="text-sm text-muted-foreground">
            {{ store.address1 }}
        </span>
        <span v-if="store.city"
              class="text-sm text-muted-foreground">
            {{ store.city }}
        </span>
        <span v-if="store.country"
              class="text-sm text-muted-foreground">
            {{ store.country }}
        </span>
        <span class="text-sm text-muted-foreground">
            ({{ store.isActive === true ? 'Active' : 'Inactive' }})
        </span>
        <span v-if="store.isSuspended === true"
              class="text-xs text-destructive">
            <OctagonX class="h-4 w-4 mr-1" />
            Suspended
        </span>
        <span class="text-xs text-muted-foreground">{{ store.id }}</span>
    </div>
    <div class="flex items-center gap-2">

        <span v-if="isListingAllStores">
            <span v-if="moreStoreDetails.isRestricted"
                  class="text-sm text-destructive flex items-center mr-2">
                <X class="h-4 w-4 mr-1" />
                Blocked
            </span>
            <span v-else
                  class="text-sm text-muted-foreground flex items-center mr-2">
                <Check class="h-4 w-4 mr-1" />
                Listed
            </span>
        </span>
        <span v-else>
            <span v-if="moreStoreDetails.isListed"
                  class="text-sm text-primary flex items-center mr-2">
                <Check class="h-4 w-4 mr-1" />
                Listed
            </span>
            <span v-else
                  class="text-sm text-muted-foreground flex items-center mr-2">
                <X class="h-4 w-4 mr-1" />
                Not Listed
            </span>
        </span>

        <div>

            <div v-if="!isListingAllStores">
                <Button v-if="!moreStoreDetails.isListed"
                        size="sm"
                        @click="upsertStore(store.id)">
                    <Plus class="h-4 w-4 mr-1" />
                    Add
                </Button>
                <Button v-else
                        size="sm"
                        variant="destructive"
                        @click="removeStore(store.id)">
                    <X class="h-4 w-4 mr-1" />
                    Remove
                </Button>
            </div>
            <div v-else>
                <Button v-if="!moreStoreDetails.isRestricted"
                        size="sm"
                        variant="destructive"
                        @click="blockStore(store.id)">
                    <X class="h-4 w-4 mr-1" />
                    Block
                </Button>
                <Button v-else
                        size="sm"
                        variant="outline"
                        @click="unblockStore(store.id)">
                    <Plus class="h-4 w-4 mr-1" />
                    Unblock
                </Button>
            </div>


            <div v-if="moreStoreDetails.overrideMarketplaceFee">
                <Badge variant="outline">
                    {{ moreStoreDetails.overrideMarketplaceFee?.ratePerOrder }}%
                </Badge>
                <Badge variant="outline">
                    +{{ moreStoreDetails.overrideMarketplaceFee?.feePerOrder }}
                </Badge>
            </div>
            <ServiceFeeDialog v-if="isServiceFeeEditable && ((!moreStoreDetails.isRestricted && isListingAllStores) || (!isListingAllStores && moreStoreDetails.isListed))"
                              :id="store.id"
                              :override-fee="moreStoreDetails.overrideMarketplaceFee ?? {}"
                              @upsert="(id, overrideMarketplaceFee) => {
                                console.log('overrideMarketplaceFee', overrideMarketplaceFee);
                                moreStoreDetails.overrideMarketplaceFee = overrideMarketplaceFee ?? undefined;
                                upsertStore(id, overrideMarketplaceFee ?? undefined);
                            }" />
        </div>

    </div>
</template>